import { useState, FC, ReactNode } from 'react';
import { ToTop } from '../modules';

type SectionProps = {
  title: string;
  children: ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => (
  <section style={{ marginBottom: '2.5rem' }}>
    <h2
      className="privacypolicy_contents_h2"
      style={{
        borderBottom: '2px solid #eee',
        paddingBottom: '0.5rem',
        marginBottom: '1.5rem',
      }}
    >
      {title}
    </h2>
    <div style={{ lineHeight: 1.8, color: '#555' }}>{children}</div>
  </section>
);

type PolicyHeaderProps = {
  title: string;
  subTitle: string;
  lastUpdate: string;
};

const PolicyHeader: FC<PolicyHeaderProps> = ({
  title,
  subTitle,
  lastUpdate,
}) => (
  <header style={{ textAlign: 'center', marginBottom: '4rem' }}>
    <h1 className="privacypolicy_title" style={{ marginBottom: '0.5rem' }}>
      {title}
    </h1>
    <h2
      className="privacypolicy_subTitle"
      style={{ marginBottom: '1rem', color: '#666' }}
    >
      {subTitle}
    </h2>
    <p
      className="privacypolicy_contents_body"
      style={{ color: '#888', fontSize: '0.9rem' }}
    >
      {lastUpdate}
    </p>
  </header>
);

type LangButtonProps = {
  onClick: () => void;
  isActive: boolean;
  children: ReactNode;
  style?: React.CSSProperties;
};

const LangButton: FC<LangButtonProps> = ({
  onClick,
  isActive,
  children,
  style,
}) => (
  <button
    onClick={onClick}
    style={{
      ...style,
      padding: '8px 16px',
      cursor: 'pointer',
      border: isActive ? '1px solid #333' : '1px solid #ccc',
      backgroundColor: isActive ? '#333' : 'white',
      color: isActive ? 'white' : '#333',
      borderRadius: '4px',
      transition: 'all 0.3s',
      minWidth: '110px',
      whiteSpace: 'nowrap',
      height: '40px',
    }}
  >
    {children}
  </button>
);

const EtanTermsOfService: FC = () => {
  const [lang, setLang] = useState<'jp' | 'en'>('jp');

  // 日本語コンテンツ
  const jpContent = {
    title: '英たん',
    subTitle: '利用規約',
    lastUpdate: '最終更新日: 2025年11月24日',
    provider: '提供者: Takumi Kanaya',
    section1Title: '1. 総則',
    section1_1Li:
      '本規約は、Takumi Kanaya（以下「当方」といいます。）が提供するアプリケーション「英たん」（以下「本アプリ」といいます。）の利用条件を定めるものです。',
    section1_2Li:
      '本アプリの利用者（以下「ユーザー」といいます。）は、本規約に同意した上で利用するものとします。',
    section1_3Li: '本規約に同意いただけない場合、本アプリを利用できません。',
    section2Title: '2. アカウント登録・管理',
    section2_1Li:
      'ユーザーは、本アプリの機能を利用するためにアカウントを作成する場合があります。',
    section2_2Li:
      'アカウント作成や認証には、Firebase Authenticationを使用する場合があります。',
    section2_3Li: 'ユーザーは、登録情報の正確性を保持する責任があります。',
    section2_4Li:
      'パスワードや認証情報の管理はユーザーの責任で行ってください。不正利用による損害について当方は責任を負いません。',
    section3Title: '3. 禁止事項',
    section3Body: 'ユーザーは、以下の行為を行ってはなりません。',
    section3_1Li: '法令に違反する行為',
    section3_2Li: '他のユーザーまたは第三者の権利・利益を侵害する行為',
    section3_3Li: '本アプリの運営を妨げる行為',
    section3_4Li: '不正アクセス、改ざん、ウイルスなど有害な行為',
    section3_5Li: 'その他当方が不適切と判断する行為',
    section4Title: '4. 知的財産権',
    section4_1Li:
      '本アプリおよびそのコンテンツの著作権・商標権・その他の権利は、Takumi Kanayaに帰属します。',
    section4_2Li:
      'ユーザーは、本アプリ内のコンテンツを無断で複製、配布、改変、商用利用してはいけません。',
    section4_3Li:
      'ユーザーが本アプリ上で作成したデータやリストは、ユーザー自身の責任で管理されます。',
    section5Title: '5. 免責事項',
    section5_1Li:
      '本アプリの利用によって生じる損害について、当方は一切責任を負いません。',
    section5_2Li:
      '本アプリは、常時の提供・正常動作を保証するものではありません。',
    section5_3Li:
      '外部サービスの利用や通信環境に起因する問題についても、当方は責任を負いません。',
    section6Title: '6. 利用停止・アカウント削除',
    section6_1Li:
      'ユーザーが本規約に違反した場合、当方はアカウントの利用停止または削除を行うことがあります。',
    section6_2Li:
      'ユーザーは、退会手続きにより自らアカウントを削除することができます。',
    section7Title: '7. プライバシー',
    section7Body:
      'ユーザーの個人情報の取り扱いについては、別途定める「プライバシーポリシー」に従います。',
    section8Title: '8. 準拠法・管轄',
    section8Body:
      '本規約は日本法に準拠し、本アプリに関して生じる紛争については千葉地方裁判所を第一審の専属管轄裁判所とします。',
    section9Title: '9. 規約の変更',
    section9Body:
      '本規約は、法令の変更やサービス改善に伴い、随時変更されることがあります。重要な変更がある場合、アプリ内で通知します。',
  };

  // 英語コンテンツ (後で追加)
  const enContent = {
    title: 'Eitan',
    subTitle: 'Terms of Service',
    lastUpdate: 'Last Updated: November 24, 2025',
    provider: 'Provider: Takumi Kanaya',
    section1Title: '1. General Provisions',
    section1_1Li:
      'These terms of service (hereinafter referred to as "these Terms") define the conditions for using the application "Eitan" (hereinafter referred to as "the App") provided by Takumi Kanaya (hereinafter referred to as "the Service Provider").',
    section1_2Li:
      'Users of the App (hereinafter referred to as "Users") shall use the App after agreeing to these Terms.',
    section1_3Li: 'If you do not agree to these Terms, you cannot use the App.',
    section2Title: '2. Account Registration and Management',
    section2_1Li:
      'Users may create an account to use the functions of the App.',
    section2_2Li:
      'Firebase Authentication may be used for account creation and authentication.',
    section2_3Li:
      'Users are responsible for maintaining the accuracy of their registered information.',
    section2_4Li:
      'Users are responsible for managing their passwords and authentication information. The Service Provider is not responsible for damages caused by unauthorized use.',
    section3Title: '3. Prohibited Matters',
    section3Body: 'Users shall not perform the following acts:',
    section3_1Li: 'Acts that violate laws and regulations',
    section3_2Li:
      'Acts that infringe on the rights or interests of other users or third parties',
    section3_3Li: 'Acts that interfere with the operation of the App',
    section3_4Li:
      'Unauthorized access, alteration, viruses, or other harmful acts',
    section3_5Li: 'Other acts that the Service Provider deems inappropriate',
    section4Title: '4. Intellectual Property Rights',
    section4_1Li:
      'Copyrights, trademark rights, and other rights related to the App and its content belong to Takumi Kanaya.',
    section4_2Li:
      'Users shall not reproduce, distribute, modify, or commercially use the content within the App without permission.',
    section4_3Li:
      "Data and lists created by users on the App are managed under the user's own responsibility.",
    section5Title: '5. Disclaimer',
    section5_1Li:
      'The Service Provider is not responsible for any damages arising from the use of the App.',
    section5_2Li:
      'The Service Provider does not guarantee the constant provision or normal operation of the App.',
    section5_3Li:
      'The Service Provider is not responsible for problems caused by the use of external services or communication environments.',
    section6Title: '6. Suspension of Use / Account Deletion',
    section6_1Li:
      'If a user violates these Terms, the Service Provider may suspend or delete the account.',
    section6_2Li:
      'Users can delete their own accounts by following the withdrawal procedure.',
    section7Title: '7. Privacy',
    section7Body:
      'The handling of user\'s personal information shall be in accordance with the separately stipulated "Privacy Policy".',
    section8Title: '8. Governing Law and Jurisdiction',
    section8Body:
      'These Terms shall be governed by Japanese law, and the Chiba District Court shall be the exclusive jurisdictional court of the first instance for any disputes arising in connection with the App.',
    section9Title: '9. Changes to the Terms',
    section9Body:
      'These Terms may be changed at any time due to changes in laws and regulations or service improvements. If significant changes occur, users will be notified within the App.',
  };

  const currentContent = lang === 'jp' ? jpContent : enContent;

  return (
    <div className="container privacypolicy" style={{ padding: '4rem 2rem' }}>
      <ToTop />
      <div
        className="privacypolicy_contents"
        style={{
          maxWidth: '800px',
          margin: '0 auto',
          fontFamily: 'sans-serif',
        }}
      >
        <div style={{ textAlign: 'right', marginBottom: '2rem' }}>
          <LangButton
            onClick={() => setLang('jp')}
            isActive={lang === 'jp'}
            style={{ marginRight: '10px' }}
          >
            日本語
          </LangButton>
          <LangButton onClick={() => setLang('en')} isActive={lang === 'en'}>
            English
          </LangButton>
        </div>

        <PolicyHeader
          title={currentContent.title}
          subTitle={currentContent.subTitle}
          lastUpdate={currentContent.lastUpdate}
        />

        <p
          className="privacypolicy_contents_body"
          style={{ marginBottom: '2.5rem' }}
        >
          {currentContent.provider}
        </p>

        <Section title={currentContent.section1Title}>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_2Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section1_3Li}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section2Title}>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_2Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_3Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section2_4Li}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section3Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section3Body}
          </p>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_2Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_3Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_4Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section3_5Li}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section4Title}>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section4_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section4_2Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section4_3Li}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section5Title}>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section5_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section5_2Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section5_3Li}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section6Title}>
          <ul className="privacypolicy_contents_ul">
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section6_1Li}
            </li>
            <li className="privacypolicy_contents_ul_li">
              {currentContent.section6_2Li}
            </li>
          </ul>
        </Section>

        <Section title={currentContent.section7Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section7Body}
          </p>
        </Section>

        <Section title={currentContent.section8Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section8Body}
          </p>
        </Section>

        <Section title={currentContent.section9Title}>
          <p className="privacypolicy_contents_body">
            {currentContent.section9Body}
          </p>
        </Section>
      </div>
    </div>
  );
};

export default EtanTermsOfService;
